import React, { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  CssBaseline,
  IconButton,
  Stack,
  Modal,
  Typography,
  Button,
  Grid,
  useTheme,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FormHelperText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";

import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";

import Topbar from "../../../../components/topbar/TopBar";

import Header from "../../../../components/header/Header";
import DataTable from "../../../../components/datatable/DataTable";
import { getTokenRefresh } from "../../../../helpers/token/refreshToken";
import {
  enabledClientUseCase,
  getClientCommentsByClientId,
  getClients,
  updateClientComment,
  updateClientApi,
} from "../../../../api/users/client/client";
import { tokens } from "../../../../theme";
import { modalStyle } from "../driver/DriverStyle";
import {
  createAdminTraveller,
  deleteTraveller,
  getTravellers,
  updateTraveller,
} from "../../../../api/users/client/traveller";
import SnackBar from "../../../../components/snackbar/SnackBar";
import { dateFormat, timeFormat } from "../../../../helpers/date/date";
//sidebar
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as themeStyles } from "@mui/material/styles";
import Sidebar from "../../../../components/sidebar/Sidebar";
import { getSunatDni } from "../../../../api/resources/sunat/sunat";
import {
  StatusEnum,
  StatusEnumName,
} from "../../../../helpers/constants/Status";
import { UserStatus } from "../../../../module_firebase/domain/usecases/status/model/UserStatusModel";
import { UpdateUserStateUseCase } from "../../../../module_firebase/domain/usecases/status/UserStatusUseCase";
import { UsersEnum } from "../../../../helpers/constants/Users";
import { UpdateStatusClientUseCase } from "./domain/UpdateStatusClientUseCase";

const ListClients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const theme = useTheme();
  const themeSidebar = themeStyles();
  const matchesSM = useMediaQuery(themeSidebar.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);
  const [UID, setUID] = useState("");
  const [STATUS, setSTATUS] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const handleModalClose = () => setOpenModal(false);

  const [openTravellerModal, setOpenTravellerModal] = useState(false);
  const handleTravellerModalClose = () => setOpenTravellerModal(false);

  const [openRatingsModal, setOpenRatingsModal] = useState(false);
  const [selectedClientRatings, setSelectedClientRatings] = useState([]);
  const [clientRatingsSearchTerm, setClientRatingsSearchTerm] = useState("");
  const [isLoadingRatings, setIsLoadingRatings] = useState(false);
  const [isUpdatingRatings, setIsUpdatingRatings] = useState(false);
  const [openChatModal, setOpenChatModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [messageSnk, setMessageSnk] = useState("");
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const vertical = "top";
  const horizontal = "center";

  const handleSnkClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const visibleClientRatings = selectedClientRatings
    .map((rating, index) => ({ ...rating, originalIndex: index }))
    .filter((rating) => {
      const searchValue = `${clientRatingsSearchTerm ?? ""}`
        .trim()
        .toLowerCase();
      if (!searchValue) return true;

      return `${rating.order_number ?? ""}`
        .trim()
        .toLowerCase()
        .includes(searchValue);
    });

  const getClientRatingRows = (ratingsSource) => {
    if (!Array.isArray(ratingsSource)) return [];

    return ratingsSource.map((rating) => ({
      ...rating,
      driver_name: rating?.name ?? "",
      rating:
        rating?.rating === 0 || rating?.rating
          ? Number(rating.rating).toFixed(1)
          : "",
      comments: rating?.comments ?? "",
    }));
  };

  const formatDateInputValue = (dateValue) => {
    if (!dateValue) return "";
    return `${dateValue}`.split("T")[0];
  };

  const calculateAgeFromBirthDate = (birthDate) => {
    const formattedBirthDate = formatDateInputValue(birthDate);
    if (!formattedBirthDate) return "";

    const [year, month, day] = formattedBirthDate.split("-").map(Number);
    if (!year || !month || !day) return "";

    const today = new Date();
    let age = today.getFullYear() - year;
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();

    if (currentMonth < month || (currentMonth === month && currentDay < day)) {
      age -= 1;
    }

    return age >= 0 ? age : "";
  };

  const validateClientRating = (rating) => {
    const numericRating = Number(rating.rating);
    if (
      rating.rating === "" ||
      Number.isNaN(numericRating) ||
      numericRating < 0 ||
      numericRating > 5
    ) {
      snackBarDialog("LA CALIFICACION DEBE ESTAR ENTRE 0.0 Y 5.0", "error");
      return false;
    }

    if ((rating.comments ?? "").length > 50) {
      snackBarDialog("EL COMENTARIO NO DEBE SUPERAR 50 CARACTERES", "error");
      return false;
    }

    return true;
  };

  const handleOpenRatingsModal = async (client) => {
    setSelectedClientRatings([]);
    setClientRatingsSearchTerm("");
    setOpenRatingsModal(true);
    setIsLoadingRatings(true);

    try {
      const token = getTokenRefresh();
      const response = await getClientCommentsByClientId(token, client.id);
      setSelectedClientRatings(getClientRatingRows(response?.data?.data));
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE LOGRO CARGAR LAS CALIFICACIONES", "error");
    } finally {
      setIsLoadingRatings(false);
    }
  };

  const handleRatingsModalClose = () => {
    setOpenRatingsModal(false);
    setSelectedClientRatings([]);
    setClientRatingsSearchTerm("");
  };

  const handleClientRatingFieldChange = (index, field, value) => {
    setSelectedClientRatings((currentRatings) =>
      currentRatings.map((rating, currentIndex) =>
        currentIndex === index ? { ...rating, [field]: value } : rating,
      ),
    );
  };

  const handleUpdateClientRating = async (index) => {
    const currentRating = selectedClientRatings[index];
    if (!currentRating) return;
    if (!validateClientRating(currentRating)) return;

    setIsUpdatingRatings(true);

    try {
      const token = getTokenRefresh();
      const formattedRating = {
        ...currentRating,
        rating: Number(Number(currentRating.rating).toFixed(1)),
        comments: currentRating.comments || null,
      };

      await updateClientComment(token, {
        id: formattedRating.id,
        clients_id: formattedRating.clients_id,
        name: formattedRating.name,
        rating: formattedRating.rating,
        comments: formattedRating.comments,
        booking_id: formattedRating.booking_id,
      });

      handleClientRatingFieldChange(
        index,
        "rating",
        formattedRating.rating.toFixed(1),
      );
      snackBarDialog("CALIFICACION ACTUALIZADA");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE LOGRO ACTUALIZAR LA CALIFICACION", "error");
    } finally {
      setIsUpdatingRatings(false);
    }
  };

  const handleChatModalClose = () => setOpenChatModal(false);

  useEffect(() => {
    const listClients = async () => {
      try {
        const token = getTokenRefresh();
        if (token) {
          const response = await getClients(token);
          setClients([]);
          let count = 0;
          Object.values(response.data).map((client, index) => {
            if (client.status !== 3) {
              client.index = ++count;
              client.clientStatus = StatusEnumName(client.status);
              setClients((olData) => [...olData, client]);
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    };
    listClients();
  }, []);

  const [traveller, setTraveller] = useState(null);

  const getListTravellers = async (uid) => {
    try {
      const token = getTokenRefresh();
      const response = await getTravellers(token, uid);
      console.log(response);
      const data = response.data.travelers.filter((traveller) => traveller.id);
      setTraveller(data);
    } catch (error) {
      console.log(error);
    }
  };

  const [uuid, setUuid] = useState();
  const [nameTravel, setNameTravel] = useState("");
  const [ageTravel, setAgeTravel] = useState(0);
  const [countryTravel, setCountryTravel] = useState("");
  const [documentTravel, setDocumentTravel] = useState("");

  const changeNameTravel = (e) => setNameTravel(e.target.value);
  const changeAgeTravel = (e) => setAgeTravel(e.target.value);
  const changeCountryTravel = (e) => setCountryTravel(e.target.value);
  const changeDocumentTravel = (e) => setDocumentTravel(e.target.value);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const [addModal, setAddModal] = useState(false);
  const handleAddModalClose = () => setAddModal(false);
  //error
  const checkName = () => isTextFieldClicked && nameTravel.length < 3;
  const checkAge = () =>
    isTextFieldClicked && (Number(ageTravel) <= 0 || Number(ageTravel) > 100);
  const checkCountry = () => isTextFieldClicked && countryTravel.length < 3;
  const checkDocument = () => isTextFieldClicked && documentTravel.length < 8;

  const [isTextFieldClicked, setIsTextFieldClicked] = useState(false);
  const handleTextFieldClick = () => setIsTextFieldClicked(true);

  const inputValidation = () => {
    return (
      nameTravel.length < 3 ||
      Number(ageTravel) <= 0 ||
      Number(ageTravel) > 100 ||
      countryTravel.length < 3 ||
      documentTravel.length < 8
    );
  };

  const searchDni = async (e) => {
    if (documentTravel.length === 8) {
      try {
        const response = await getSunatDni(documentTravel);

        const data = response.data;
        setNameTravel(
          `${data.nombres} ${data.apellidoPaterno} ${data.apellidoMaterno}`,
        );
      } catch (error) {
        snackBarDialog("DNI no encontrado", "error");
      }
    } else {
      snackBarDialog("DNI de 8 digitos", "error");
    }
  };

  const addTraveller = async (e) => {
    try {
      e.preventDefault();
      handleTextFieldClick();
      if (inputValidation()) return snackBarDialog("ingrese los campos");

      const response = await createAdminTraveller(
        nameTravel.toUpperCase(),
        ageTravel,
        documentTravel,
        countryTravel.toUpperCase(),
        uuid,
      );

      snackBarDialog("USUARIO AGREGADO");
      handleAddModalClose();
    } catch (error) {
      handleAddModalClose();
    }
  };

  const columns = [
    { field: "index", headerName: "ID", width: 50 },
    {
      headerName: "NOMBRE",
      field: "name",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 130,
    },
    {
      headerName: "APELLIDOS",
      field: "lastname",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 200,
    },
    {
      headerName: "EMAIL",
      field: "email",
      width: 200,
    },
    {
      headerName: "TELEFONO",
      field: "phone",
      width: 110,
    },
    {
      headerName: "PAIS",
      field: "country",
      headerAlign: "center",
      align: "center",
      width: 100,
    },
    {
      headerName: "R",
      headerAlign: "center",
      field: "rating",
      width: 50,
      align: "center",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Button
            size="small"
            color="secondary"
            variant="contained"
            sx={{ minWidth: 32, p: "2px 6px" }}
            onClick={() => handleOpenRatingsModal(row)}
          >
            {row.rating}
          </Button>
        );
      },
    },

    {
      headerName: "SERVICIOS",
      field: "num_booking",
      width: 90,
      align: "center",
    },
    {
      headerName: "HABILITADO",
      field: "verified",
      width: 80,
      align: "center",
      renderCell: ({ row: { verified } }) => {
        return <p>{verified === 1 ? "SI" : "NO"}</p>;
      },
    },
    {
      headerName: "ESTADO",
      field: "clientStatus",
      width: 90,
      align: "center",
    },
    {
      headerName: "REGISTRADO",
      field: "created_at",
      width: 120,
      align: "center",
      renderCell: ({ row: { created_at } }) => {
        return (
          <div>
            {dateFormat(new Date(created_at))}{" "}
            {timeFormat(new Date(created_at))}
          </div>
        );
      },
    },
    {
      headerName: "OPCIONES",
      field: "actions",
      headerAlign: "center",
      width: 180,
      align: "center",
      renderCell: ({ row: { id, status } }) => {
        return (
          <Stack direction="row" spacing={1}>
            <IconButton
              color="success"
              aria-label="add"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={() => {
                navigate(`/admin/client/${id}`);
              }}
            >
              <BorderColorOutlinedIcon />
            </IconButton>
            <IconButton
              color="success"
              aria-label="travel"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={async () => {
                await getListTravellers(id);
                setOpenTravellerModal(true);
              }}
            >
              <Diversity3Icon />
            </IconButton>

            <IconButton
              color="success"
              aria-label="travel"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={async () => {
                setUuid(id);
                setAddModal(true);
              }}
            >
              <GroupAddOutlinedIcon />
            </IconButton>

            <IconButton
              aria-label="delete"
              component="label"
              sx={{ background: "#AA151B" }}
              onClick={() => {
                setUID(id);
                setSTATUS(status);
                setOpenModal(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  const updateStatus = async (status) => {
    setIsLoading(true);
    try {
      const resp = await UpdateStatusClientUseCase(UID, status);

      if (resp.message) {
        const userStatus = new UserStatus(UID, UsersEnum.CLIENT, status);
        await UpdateUserStateUseCase(userStatus);
        switch (status) {
          case 1 && 2:
            snackBarDialog("ESTADO ACTUALIZADO");
            clients.map((client) => {
              if (client.id === UID) {
                client.status = status;
                client.clientStatus = StatusEnumName(status);
              }
            });
            break;

          case 3:
            setClients(clients.filter((client) => client.id !== UID));
            snackBarDialog("CONDUCTOR ELIMINADO");
            break;
        }
      } else {
        snackBarDialog("ERROR: NO SE LOGRO ACTUALIZAR EL ESTADO", "error");
      }

      setIsLoading(false);
      setOpenModal(false);
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE LOGRO ACTUALIZAR EL ESTADO", "error");
      setIsLoading(false);
    }
  };

  const enabledClient = async () => {
    setIsLoading(true);
    try {
      await enabledClientUseCase(UID);
      snackBarDialog("CLIENTE HABILITADO");
      setIsLoading(false);
      setOpenModal(false);
    } catch (error) {
      snackBarDialog("Error no se podido Habilitar");
      setIsLoading(false);
    }
  };

  const modalTravelStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    border: "1px solid #000",
    borderRadius: "10px",
    boxShadow: 24,
    p: 1,
    overflow: "scroll",
  };

  const editTraveller = async (traveller) => {
    try {
      setIsLoading(true);
      const token = getTokenRefresh();
      await updateTraveller(token, traveller);
      snackBarDialog("DATOS DE VIAJERO ACTUALIZADO");
      setIsLoading(false);
      // const index = traveller.findIndex(
      //   (travel) => travel.id === putTraveller.id
      // );
      // traveller[index] = putTraveller;
      // handleCloseEditModal();
      // setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      snackBarDialog("ERROR NO SE LOGRO ACTUALIZAR DATOS", "error");
      console.log(error);
    }
  };

  const eliminateTraveller = async (id) => {
    try {
      setIsLoading(true);
      const token = getTokenRefresh();
      await deleteTraveller(token, id);
      // const index = traveller.findIndex((travel) => travel.id === id);
      // traveller.splice(index, 1);
      snackBarDialog("VIAJERO ELIMINADO");
      handleTravellerModalClose();
      setIsLoading(false);
      // setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      snackBarDialog("ERROR NO SE LOGRO ELIMINAR VIAJERO", "error");
      console.log(error);
    }
  };

  return (
    <>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box m="20px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Header title="LISTA CLIENTES" subtitle="" />
            </Box>
            <Box>
              <Box>
                <DataTable data={clients} columns={columns} />
              </Box>
              <Modal
                open={openRatingsModal}
                onClose={handleRatingsModalClose}
                aria-labelledby="modal-ratings-title"
              >
                <Box
                  sx={{
                    ...modalStyle,
                    width: { xs: "90%", sm: 760 },
                    maxHeight: "80vh",
                    overflowY: "auto",
                    backgroundColor: colors.primary[400],
                  }}
                >
                  <Typography
                    id="modal-ratings-title"
                    variant="h5"
                    component="h2"
                    textAlign="center"
                    sx={{ fontWeight: 900 }}
                    mb={2}
                  >
                    Lista de Calificaciones
                  </Typography>
                  {isLoadingRatings ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress color="secondary" />
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      <TextField
                        value={clientRatingsSearchTerm}
                        fullWidth
                        label="Buscar pedido"
                        variant="outlined"
                        size="small"
                        onChange={(event) =>
                          setClientRatingsSearchTerm(event.target.value)
                        }
                      />
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          borderRadius: 1,
                          border: "1px solid rgba(255, 255, 255, 0.18)",
                          overflowX: "auto",
                          backgroundColor: colors.primary[500],
                        }}
                      >
                        <Table
                          size="small"
                          aria-label="tabla de calificaciones"
                        >
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: colors.primary[600],
                              }}
                            >
                              <TableCell sx={{ fontWeight: 800 }}>
                                PEDIDO
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>
                                CONDUCTOR
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800, minWidth: 90 }}>
                                CALIFICACION
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 800, minWidth: 230 }}
                              >
                                COMENTARIO
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800 }}>
                                ACCIONES
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {visibleClientRatings.length > 0 ? (
                              visibleClientRatings.map((rating) => (
                                <TableRow
                                  key={`${rating.order_number}-${rating.originalIndex}`}
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      borderBottom: 0,
                                    },
                                  }}
                                >
                                  <TableCell>{rating.order_number}</TableCell>
                                  <TableCell>{rating.driver_name}</TableCell>
                                  <TableCell>
                                    <TextField
                                      value={rating.rating ?? ""}
                                      fullWidth
                                      type="number"
                                      label="Calificacion"
                                      variant="outlined"
                                      margin="dense"
                                      inputProps={{
                                        min: 0,
                                        max: 5,
                                        step: 0.1,
                                      }}
                                      disabled={isUpdatingRatings}
                                      onChange={(event) =>
                                        handleClientRatingFieldChange(
                                          rating.originalIndex,
                                          "rating",
                                          event.target.value,
                                        )
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      value={rating.comments ?? ""}
                                      fullWidth
                                      label="Comentario"
                                      variant="outlined"
                                      margin="dense"
                                      inputProps={{ maxLength: 50 }}
                                      disabled={isUpdatingRatings}
                                      onChange={(event) =>
                                        handleClientRatingFieldChange(
                                          rating.originalIndex,
                                          "comments",
                                          event.target.value,
                                        )
                                      }
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      justifyContent="flex-end"
                                    >
                                      <Button
                                        color="secondary"
                                        variant="contained"
                                        disabled={isUpdatingRatings}
                                        onClick={() =>
                                          handleUpdateClientRating(
                                            rating.originalIndex,
                                          )
                                        }
                                      >
                                        {isUpdatingRatings
                                          ? "ACTUALIZANDO..."
                                          : "ACTUALIZAR"}
                                      </Button>
                                      <IconButton
                                        color="success"
                                        aria-label="chat"
                                        sx={{ background: "#ffff" }}
                                        onClick={() => setOpenChatModal(true)}
                                      >
                                        <ChatOutlinedIcon />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography textAlign="center" py={2}>
                                    No hay calificaciones registradas.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  )}
                </Box>
              </Modal>
              <Modal
                open={openChatModal}
                onClose={handleChatModalClose}
                aria-labelledby="modal-chat-title"
              >
                <Box
                  sx={{
                    ...modalStyle,
                    width: { xs: "90%", sm: 420 },
                    backgroundColor: colors.primary[400],
                  }}
                >
                  <Typography
                    id="modal-chat-title"
                    variant="h5"
                    component="h2"
                    textAlign="center"
                    sx={{ fontWeight: 900 }}
                  >
                    Chat del Pedido
                  </Typography>
                </Box>
              </Modal>
              <SnackBar
                stateSnk={stateSnk}
                vertical={vertical}
                horizontal={horizontal}
                duration={4000}
                handleClose={handleSnkClose}
                severity={severity}
                messageSnk={messageSnk}
              />
              <Modal open={openModal} onClose={handleModalClose}>
                <Box backgroundColor={colors.primary[400]} sx={modalStyle}>
                  <Typography variant="h6" component="h2" textAlign={"center"}>
                    ELIMINAR CLIENTE?
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={3}>
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={enabledClient}
                        disabled={isLoading}
                      >
                        VERIFICAR
                      </Button>
                    </Grid>
                    <Grid item xs={4} md={3} textAlign="center">
                      {STATUS === 1 ? (
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => updateStatus(StatusEnum.SUSPENDIDO)}
                          disabled={isLoading}
                        >
                          SUSPENDER
                        </Button>
                      ) : (
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => updateStatus(StatusEnum.ACTIVO)}
                          disabled={isLoading}
                        >
                          HABILITAR
                        </Button>
                      )}
                    </Grid>
                    <Grid item xs={4} md={3} textAlign="center">
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => updateStatus(StatusEnum.ELIMINADO)}
                        disabled={isLoading}
                      >
                        ELIMINAR
                      </Button>
                    </Grid>
                    <Grid item xs={4} md={3} textAlign="center">
                      <Button
                        color="error"
                        variant="contained"
                        onClick={handleModalClose}
                        disabled={isLoading}
                      >
                        CANCELAR
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Modal>
              
              <Modal
                open={openTravellerModal}
                onClose={handleTravellerModalClose}
              >
                <Box
                  backgroundColor={colors.primary[400]}
                  sx={modalTravelStyle}
                  maxHeight={"80%"}
                >
                  <Typography variant="h6" component="h2" textAlign={"center"}>
                    Lista de Viajeros?
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    {traveller &&
                      traveller.map((travel, index) => (
                        <Fragment key={index}>
                          <Grid item xs={12} md={8} textAlign="center">
                            <TextField
                              value={travel.name}
                              onChange={(e) => {
                                const newTraveller = [...traveller];
                                newTraveller[index].name = e.target.value;
                                setTraveller(newTraveller);
                              }}
                              fullWidth
                              type="text"
                              label={t("Nombre")}
                              inputProps={{
                                style: { textTransform: "uppercase" },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4} textAlign="center">
                            <TextField
                              value={formatDateInputValue(travel.birth_date)}
                              onChange={(e) => {
                                const newTraveller = [...traveller];
                                newTraveller[index].birth_date = e.target.value;
                                setTraveller(newTraveller);
                              }}
                              fullWidth
                              type="date"
                              label={t("Fecha de nacimiento")}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={2} textAlign="center">
                            <TextField
                              value={calculateAgeFromBirthDate(
                                travel.birth_date,
                              )}
                              fullWidth
                              type="text"
                              label={t("Edad")}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={5} textAlign="center">
                            <TextField
                              value={travel.country}
                              onChange={(e) => {
                                const newTraveller = [...traveller];
                                newTraveller[index].country = e.target.value;
                                setTraveller(newTraveller);
                              }}
                              fullWidth
                              type="text"
                              label={t("Nacionalidad")}
                              inputProps={{
                                style: { textTransform: "uppercase" },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={5} textAlign="center">
                            <TextField
                              value={travel.document}
                              onChange={(e) => {
                                const newTraveller = [...traveller];
                                newTraveller[index].document = e.target.value;
                                setTraveller(newTraveller);
                              }}
                              fullWidth
                              type="text"
                              label={t("DNI")}
                              inputProps={{
                                style: { textTransform: "uppercase" },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} textAlign="center">
                            <Button
                              fullWidth
                              variant="contained"
                              color="success"
                              onClick={(e) => {
                                e.preventDefault();
                                editTraveller(travel);
                              }}
                              disabled={isLoading}
                            >
                              EDITAR
                            </Button>
                          </Grid>
                          <Grid item xs={12} md={6} textAlign="center">
                            <Button
                              fullWidth
                              variant="contained"
                              color="error"
                              onClick={(e) => {
                                e.preventDefault();
                                eliminateTraveller(travel.id);
                              }}
                              disabled={isLoading}
                            >
                              ELIMINAR
                            </Button>
                          </Grid>
                        </Fragment>
                      ))}
                  </Grid>
                </Box>
              </Modal>

              {/* AGREGAR TRAVEL */}
              <Modal open={addModal} onClose={handleAddModalClose}>
                <Box
                  backgroundColor={colors.primary[400]}
                  sx={modalTravelStyle}
                  maxHeight={"80%"}
                >
                  <Typography variant="h6" component="h2" textAlign={"center"}>
                    AGREGAR VIAJERO
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <form>
                      <Box gap="20px" sx={{ margin: "5px" }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={12}>
                            <TextField
                              value={nameTravel}
                              type="text"
                              label={"NOMBRE"}
                              name="nameTravel"
                              onChange={changeNameTravel}
                              fullWidth
                              inputProps={{
                                style: { textTransform: "uppercase" },
                              }}
                              error={checkName()}
                              helperText={
                                checkName() ? "Ingrese su Nombre" : ""
                              }
                              onClick={handleTextFieldClick}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <TextField
                              value={ageTravel}
                              type="number"
                              label={"EDAD"}
                              name="ageTravel"
                              onChange={changeAgeTravel}
                              error={checkAge()}
                              helperText={checkAge() ? "Edad" : ""}
                              onClick={handleTextFieldClick}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <TextField
                              value={countryTravel}
                              type="text"
                              label={"country"}
                              name="countryTravel"
                              onChange={changeCountryTravel}
                              fullWidth
                              error={checkCountry()}
                              helperText={
                                checkCountry() ? "Ingrese su Pais" : ""
                              }
                              onClick={handleTextFieldClick}
                              inputProps={{
                                style: { textTransform: "uppercase" },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={12}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel htmlFor="input-document">
                                DNI
                              </InputLabel>
                              <OutlinedInput
                                value={documentTravel}
                                name="documentTravel"
                                maxRows={1}
                                id="control-dni"
                                onChange={changeDocumentTravel}
                                inputProps={{ maxLength: 16 }}
                                error={checkDocument()}
                                onClick={handleTextFieldClick}
                                endAdornment={
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle visibility"
                                      onMouseDown={handleMouseDownPassword}
                                      edge="end"
                                      onClick={searchDni}
                                    >
                                      <SendOutlinedIcon />
                                    </IconButton>
                                  </InputAdornment>
                                }
                                label="DNI"
                              />
                              {checkDocument() ? (
                                <FormHelperText sx={{ color: "red" }}>
                                  Ingrese DNI/Passaport
                                </FormHelperText>
                              ) : (
                                <></>
                              )}
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Button
                              fullWidth
                              type="submit"
                              color="success"
                              variant="contained"
                              onClick={addTraveller}
                            >
                              agregar
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* <SnackBar
          stateSnk={stateSnk}
          vertical={vertical}
          horizontal={horizontal}
          duration={4000}
          handleClose={handleCloseSnk}
          severity={severity}
          messageSnk={messageSnk}
        /> */}
                    </form>
                  </Grid>
                </Box>
              </Modal>
            </Box>
          </Box>
        </main>
      </div>
    </>
  );
};

export default ListClients;
